import { useEffect, useMemo, useState, useCallback } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { useLoading, useServerError } from 'shared/hooks'

import { GET_INTERNAL_DOMAINS, useFindUserByEmailLazyQuery } from 'modules/Users/graphql/queries'
import { GET_ROLES } from 'modulesCommon/graphql/queries'

import { useCreateOktaUser, useCreateUser, useAssignUserOktaGroup } from 'modules/Users/graphql/mutations'

import { currentUserVar, userRolesVar, notificationMessageVar } from 'modulesCommon/store'

import { formatRolesToOptions, formatUserData, formatOktaUserData } from 'modules/Users/utils'

import { IRole } from 'shared/interfaces'

import { userSchema, isEmail } from '../constants/validation'
import { IUserForm } from '../types'

const useServiceCreateUser = () => {
  const navigate = useNavigate()

  const [activationCode, setShowActivation] = useState('')

  const userRoles = useReactiveVar(userRolesVar)
  const loggedUser = useReactiveVar(currentUserVar)

  const { data: rolesData, loading: isRolesLoading, error: rolesError } = useQuery<{ getRoles: IRole[] }, {}>(GET_ROLES)
  const {
    data: internalDomainsData,
    loading: isInternalDomainLoading,
    error: internalDomainError
  } = useQuery<{ getInternalDomains: string[] }, {}>(GET_INTERNAL_DOMAINS)

  const { mutate: createUser, loading: isCreationLoading, error: isCreationError } = useCreateUser()
  const { mutate: createOktaUser, loading: isOktaCreationLoading, error: isOktaCreationError } = useCreateOktaUser()
  const { mutate: assignOktaUser, loading: isAssignOktaLoading } = useAssignUserOktaGroup()
  const [findUser] = useFindUserByEmailLazyQuery()

  const methods = useForm<IUserForm>({
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      all_campaign: true,
    },
    resolver: yupResolver(
      userSchema((email) => findUser({ variables: { email }})
        .then(({ data }) => !(typeof data?.findUserByEmail?.id === 'string'))
      )),
  })

  const emailValue = methods.watch('email')

  const domainsRegexp = useMemo(
    () => new RegExp(`@(${internalDomainsData?.getInternalDomains.join('|')})\\s*$`, 'i'),
    [internalDomainsData?.getInternalDomains]
  )

  const isExternalEmail = useMemo<boolean>(
    () => isEmail(emailValue) && !emailValue.match(domainsRegexp),
    [domainsRegexp, emailValue],
  )

  const userRolesOptions = useMemo(
    () => formatRolesToOptions(userRoles, isExternalEmail, loggedUser?.role?.role_id),
    [isExternalEmail, userRoles, loggedUser]
  )

  const onAssignCreateUser = useCallback(
    async (email: string) => {
      try {
        const { data } = await assignOktaUser({
          variables: { email }
        })

        return data
      } catch {
        return
      }
    },
    [assignOktaUser]
  )

  const onCreateUser = useCallback(
    async (values: IUserForm, activation?: string) => {
      const { data } = await createUser({
        variables: { input: formatUserData(values, isExternalEmail) }
      })

      await onAssignCreateUser(values.email)

      const isSuccess = isExternalEmail ? data?.createUser && activation : data?.createUser

      if (data?.createUser?.id) {
        navigate('/admin/users')
      }

      notificationMessageVar({
        type: isSuccess ? 'success' : 'warning',
        message: isSuccess
          ? `User successfully created. ${isExternalEmail ? 'An email with an activation link has been sent.' : ''}`
          : 'User already exists.',
        title: isSuccess ? 'Success' : 'Warning'
      })

      return data
    },
    [navigate, createUser, onAssignCreateUser, isExternalEmail]
  )

  const onCreateOktaUser = useCallback(
    async (values: IUserForm) => {
      const { data } = await createOktaUser({ variables: { input: formatOktaUserData(values) } })

      if (data?.createOktaUser?.profile?.email) {
        return data.createOktaUser
      } else {
        return
      }
    },
    [createOktaUser]
  )

  const onCreateExternalUser = useCallback(
    async (values: IUserForm) => {
      const oktaUserData = await onCreateOktaUser(values)

      if (oktaUserData) {
        const userData = await onCreateUser(values, oktaUserData?.activation as string | undefined)

        if (userData && oktaUserData?.activation) {
          setShowActivation(oktaUserData?.activation)
        }
      }
    },
    [onCreateOktaUser, onCreateUser]
  )

  const onCreate = useCallback(
    (values: IUserForm) => (isExternalEmail ? onCreateExternalUser(values) : onCreateUser(values)),
    [isExternalEmail, onCreateExternalUser, onCreateUser]
  )

  const isSubmitButtonDisabled = useMemo(
    () => isCreationLoading || isOktaCreationLoading,
    [isCreationLoading, isOktaCreationLoading]
  )

  useEffect(() => {
    if (isExternalEmail) methods.setValue('role', userRoles?.find((el: IRole) => el.default)?.id || '')
  }, [isExternalEmail, methods, userRoles])

  useEffect(() => {
    if (rolesData) userRolesVar(rolesData.getRoles)
  }, [rolesData])

  useEffect(() => {
    if (isCreationError?.message || isOktaCreationError?.message) {
      notificationMessageVar({
        type: 'warning',
        message: (isCreationError || isOktaCreationError)?.message || '',
        title: 'Warning'
      })
    }
  }, [isCreationError, isOktaCreationError])

  useLoading({
    isLoading:
      isRolesLoading ||
      isCreationLoading ||
      isOktaCreationLoading ||
      isInternalDomainLoading ||
      isAssignOktaLoading
  })
  useServerError([rolesError, internalDomainError])

  return {
    control: methods.control,
    methods,
    isExternalEmail,
    activationCode,
    userRolesOptions,
    handleSubmit: methods.handleSubmit,
    onCreate,
    isSubmitButtonDisabled,
  }
}

export default useServiceCreateUser
